import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ConversationSummary, Project } from '../../models/conversation.model';
import { ChatApiService } from '../../services/chat-api.service';
import { UserInfoService } from '../../../../../../../../src/app/services/user-info.service';
import { ContextMenuItem } from '../../models/conversation.model';

@Component({
	selector: 'app-conversation-sidebar',
	templateUrl: './conversation-sidebar.component.html',
	styleUrls: ['./conversation-sidebar.component.scss'],
})
export class ConversationSidebarComponent implements OnInit {
	@Input() conversations: ConversationSummary[] = [];
	@Input() projects: Project[] = [];
	@Input() activeConversationId?: string;
	@Input() activeProjectId?: string;
	@Output() conversationSelected = new EventEmitter<string>();
	@Output() conversationDeleted = new EventEmitter<string>();
	@Output() newChatRequested = new EventEmitter<void>();
	@Output() projectsChanged = new EventEmitter<void>();
	@Output() projectSelected = new EventEmitter<string>();
	@ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

	// ── Search state ──
	searchQuery = '';
	searchActive = false;

	// ── New project state ──
	newProjectName = '';
	showNewProjectInput = false;

	// ── Conversation context menu state ──
	contextMenuConvId: string | null = null;
	contextMenuConvHasProject = false;
	contextMenuConvProjectId: string | null = null;
	contextMenuConvIsPinned = false;
	contextMenuX = 0;
	contextMenuY = 0;

	// ── Conversation rename state ──
	renamingConvId: string | null = null;
	renameValue = '';

	// ── Project context menu state ──
	projectContextMenuId: string | null = null;
	projectContextMenuX = 0;
	projectContextMenuY = 0;

	// ── Project rename state ──
	projectRenameId: string | null = null;
	projectRenameValue = '';

	internalUserGUID = '';

	constructor(
		private chatApi: ChatApiService,
		private userService: UserInfoService
	) {}

	async ngOnInit(): Promise<void> {
		this.internalUserGUID = (await this.userService.getCurrentLoggedInUser()).internalUserGUID ?? '';
	}

	/** Items for the project context menu */
	get projectContextMenuItems(): ContextMenuItem[] {
		return [
			{ id: 'rename-project', label: 'Rename', icon: 'edit' },
			{ id: 'delete-project', label: 'Delete', icon: 'delete', danger: true, dividerBefore: true },
		];
	}

	// ── Keyboard & document listeners ──

	@HostListener('window:keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
			event.preventDefault();
			this.focusSearch();
		}
	}

	@HostListener('document:click')
	onDocumentClick(): void {
		this.contextMenuConvId = null;
	}

	// ── Computed getters ──

	get isSearching(): boolean {
		return this.searchActive || this.searchQuery.trim().length > 0;
	}

	get filteredConversations(): ConversationSummary[] {
		const q = this.searchQuery.trim().toLowerCase();
		if (!q) return [];
		return this.conversations.filter((c) => c.title.toLowerCase().includes(q) || (c.lastMessage && c.lastMessage.toLowerCase().includes(q)));
	}

	get ungroupedConversations(): ConversationSummary[] {
		return this.conversations
			.filter((c) => !c.projectId)
			.sort((a, b) => {
				if (a.pinned && !b.pinned) return -1;
				if (!a.pinned && b.pinned) return 1;
				return (b.timestamp || '').localeCompare(a.timestamp || '');
			});
	}

	// ── Public actions ──

	onSelectConversation(id: string): void {
		this.contextMenuConvId = null;
		this.searchQuery = '';
		this.searchActive = false;
		this.conversationSelected.emit(id);
	}

	onNewChat(): void {
		this.newChatRequested.emit();
	}

	onSelectProject(id: string): void {
		this.projectSelected.emit(id);
	}

	// ── Search ──

	focusSearch(): void {
		this.searchActive = true;
		setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
	}

	clearSearch(): void {
		this.searchQuery = '';
		this.searchActive = false;
	}

	onSearchKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			this.clearSearch();
		}
	}

	// ── New project ──

	toggleNewProjectInput(): void {
		this.showNewProjectInput = !this.showNewProjectInput;
		this.newProjectName = '';
	}

	async submitNewProject(): Promise<void> {
		const name = this.newProjectName.trim();
		if (!name) return;
		await this.chatApi.createProject({ name, internalUserGUID: this.internalUserGUID });
		this.showNewProjectInput = false;
		this.newProjectName = '';
		this.projectsChanged.emit();
	}

	onNewProjectKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter') this.submitNewProject();
		if (event.key === 'Escape') {
			this.showNewProjectInput = false;
			this.newProjectName = '';
		}
	}

	// ── Conversation context menu ──

	openContextMenu(event: MouseEvent, convId: string): void {
		event.preventDefault();
		event.stopPropagation();
		this.contextMenuConvId = convId;
		this.projectContextMenuId = null;
		const hostRect = (event.currentTarget as HTMLElement).closest('.sidebar')?.getBoundingClientRect() ?? { left: 0, top: 0 };
		this.contextMenuX = event.clientX - hostRect.left;
		this.contextMenuY = event.clientY - hostRect.top;
		const conv = this.conversations.find((c) => c.id === convId);
		this.contextMenuConvHasProject = !!conv?.projectId;
		this.contextMenuConvProjectId = conv?.projectId ?? null;
		this.contextMenuConvIsPinned = !!conv?.pinned;
	}

	// ── Move to project ──

	async moveToProject(convId: string, projectId: string | undefined): Promise<void> {
		this.contextMenuConvId = null;
		await this.chatApi.assignConversationToProject({ conversationId: convId, projectId });
		this.projectsChanged.emit();
	}

	// ── Conversation rename ──

	startRenameConversation(): void {
		if (!this.contextMenuConvId) return;
		const conv = this.conversations.find((c) => c.id === this.contextMenuConvId);
		this.renamingConvId = this.contextMenuConvId;
		this.renameValue = conv?.title ?? '';
		this.contextMenuConvId = null;
	}

	async submitRenameConversation(title: string): Promise<void> {
		if (!this.renamingConvId) return;
		const id = this.renamingConvId;
		this.renamingConvId = null;
		this.renameValue = '';
		await this.chatApi.renameConversation({ conversationId: id, title });
		this.projectsChanged.emit();
	}

	cancelConvRename(): void {
		this.renamingConvId = null;
		this.renameValue = '';
	}

	// ── Conversation pin ──

	async togglePinConversation(): Promise<void> {
		if (!this.contextMenuConvId) return;
		const convId = this.contextMenuConvId;
		const newPinned = !this.contextMenuConvIsPinned;
		this.contextMenuConvId = null;
		await this.chatApi.pinConversation({ conversationId: convId, pinned: newPinned });
		this.projectsChanged.emit();
	}

	// ── Conversation delete ──

	async deleteConversation(): Promise<void> {
		if (!this.contextMenuConvId) return;
		const convId = this.contextMenuConvId;
		this.contextMenuConvId = null;
		await this.chatApi.deleteConversation({ conversationId: convId });
		this.conversationDeleted.emit(convId);
		this.projectsChanged.emit();
	}

	// ── Project context menu ──

	openProjectContextMenu(event: MouseEvent, project: Project): void {
		event.preventDefault();
		event.stopPropagation();
		this.projectContextMenuId = project.id;
		this.contextMenuConvId = null;
		const hostRect = (event.currentTarget as HTMLElement).closest('.sidebar')?.getBoundingClientRect() ?? { left: 0, top: 0 };
		this.projectContextMenuX = event.clientX - hostRect.left;
		this.projectContextMenuY = event.clientY - hostRect.top;
	}

	/** Dispatch project context menu actions */
	onProjectMenuAction(actionId: string): void {
		switch (actionId) {
			case 'rename-project':
				this.startRenameProject();
				break;
			case 'delete-project':
				this.deleteProject();
				break;
		}
	}

	// ── Project rename ──

	private startRenameProject(): void {
		const project = this.projects.find((p) => p.id === this.projectContextMenuId);
		this.projectRenameId = this.projectContextMenuId;
		this.projectRenameValue = project?.name ?? '';
		this.projectContextMenuId = null;
	}

	async submitProjectRename(name: string): Promise<void> {
		if (!this.projectRenameId) return;
		const id = this.projectRenameId;
		this.projectRenameId = null;
		this.projectRenameValue = '';
		await this.chatApi.updateProject({ projectId: id, name });
		this.projectsChanged.emit();
	}

	cancelProjectRename(): void {
		this.projectRenameId = null;
		this.projectRenameValue = '';
	}

	// ── Project delete ──

	private async deleteProject(): Promise<void> {
		if (!this.projectContextMenuId) return;
		const id = this.projectContextMenuId;
		this.projectContextMenuId = null;
		await this.chatApi.deleteProject({ projectId: id });
		this.projectsChanged.emit();
	}
}
